import { useState } from "react";
import { ApiError, api, isSubscriptionUpdated } from "../api/client";

// Five-tier card grid. Free/Standard/Managed Isolated have self-serve
// checkout; BYO and Enterprise route to /contact because they are
// provisioned by Sequesign operators on request.
//
// Library storage is one flat price ($0.10 / GB-month) on every paid
// tier. Retention is no longer a pricing variable; the customer
// controls receipt-library retention via pruning policies (Phase 3.5).

type SelfServeTier = "standard" | "managed_isolated";

const CONTACT_URL = "/contact";

export function UpgradePage(props: { currentTier: string; onBack: () => void }) {
  const [busy, setBusy] = useState<SelfServeTier | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  async function startCheckout(tier: SelfServeTier) {
    setError(null);
    setInfo(null);
    setBusy(tier);
    try {
      const result = await api.billingCheckout({
        tier,
        pricing_model: "per_signature"
      });
      if (isSubscriptionUpdated(result)) {
        // In-place subscription update (paid → higher paid). No
        // Stripe redirect; the webhook will refresh the local tier
        // within a minute or two.
        setBusy(null);
        setInfo(
          `Tier changed to ${result.tier === "managed_isolated" ? "Managed Isolated" : "Standard"}. Stripe is processing; the new tier will appear within a minute.`
        );
        return;
      }
      window.location.href = result.checkout_url;
    } catch (err) {
      setBusy(null);
      if (err instanceof ApiError) {
        if (err.code === "billing_not_configured") {
          setError(
            "Billing isn't configured on this deployment yet. Once Stripe is wired up, the upgrade button will route to checkout."
          );
        } else if (err.code === "downgrade_via_support") {
          setError("Downgrades aren't self-serve. Contact support to change to a lower-tier plan.");
        } else if (err.code === "already_on_tier") {
          setError("You're already on this tier.");
        } else {
          setError(err.message);
        }
      } else {
        setError("Could not start checkout.");
      }
    }
  }

  return (
    <>
      <div className="panel">
        <h2 className="section-title">Choose your trust model</h2>
        <p className="muted">
          Library storage is $0.10 per GB-month on every paid tier. Configure receipt retention via
          pruning policies (time-based, size-based, or both).
        </p>
      </div>

      <TierCard
        name="Free"
        sub="Developer tier"
        price="$0"
        priceSub="/ month"
        description="Try the protocol with no commitment."
        features={[
          "1,000 signatures / month",
          "10 MB library storage (storage pauses at the limit)",
          "Up to 1 MB evidence per receipt",
          "Standard witness service",
          "Email support"
        ]}
        current={props.currentTier === "free"}
        cta={null}
      />

      <TierCard
        name="Standard"
        sub="For builders"
        price="$5"
        priceSub="/ month"
        description="5,000 witnesses + 1 GB included, then $0.001 each."
        features={[
          "5,000 witnesses / month included",
          "1 GB library storage included",
          "Up to 4 MB evidence per receipt",
          "$0.001 per witness over 5,000",
          "$0.10 per GB-month over 1 GB",
          "Standard witness at witness.sequesign.com",
          "Email support"
        ]}
        current={props.currentTier === "standard"}
        cta={{
          kind: "checkout",
          label: busy === "standard" ? "Routing to Stripe…" : "Get started",
          disabled: busy !== null,
          onClick: () => startCheckout("standard")
        }}
      />

      <TierCard
        name="Managed Isolated"
        sub="Dedicated witness for your data"
        featured
        price="$25"
        priceSub="/ month + $0.002 per signature"
        description="Your own dedicated witness instance with isolated keys."
        features={[
          "10 GB library storage included",
          "Up to 4 MB evidence per receipt",
          "$0.10 per GB-month for additional storage",
          "Configurable retention (time-based or size-based pruning)",
          "Dedicated witness instance with isolated keys",
          "Priority support"
        ]}
        current={props.currentTier === "managed_isolated"}
        cta={{
          kind: "checkout",
          label: busy === "managed_isolated" ? "Routing to Stripe…" : "Get started",
          disabled: busy !== null,
          onClick: () => startCheckout("managed_isolated")
        }}
      />

      <TierCard
        name="Bring Your Own Infrastructure"
        sub="Data residency for regulated deployments"
        tag="For data sovereignty"
        price="Contact us"
        description="Keep your receipts and evidence in your own cloud or datacenter. We work with you on a deployment that fits your data-residency requirements — your data stays under your control while Sequesign provides the protocol, witness, SDK, and support."
        features={[
          "Your data stays in your infrastructure",
          "Hash-only by default — evidence content need never reach Sequesign",
          "Configurable evidence size limits",
          "Independent witness signing by Sequesign",
          "Configurable retention (time-based or size-based pruning)",
          "Protocol updates and integration support"
        ]}
        current={props.currentTier === "byo"}
        cta={{ kind: "contact", label: "Talk to us", href: CONTACT_URL }}
      />

      <TierCard
        name="Enterprise"
        sub="Custom for your scale"
        tag="For complex needs"
        price="Custom"
        description="Annual contracts, dedicated support, custom SLA."
        features={[
          "All deployment options",
          "Custom storage quotas and rates",
          "Custom evidence size limits",
          "Dedicated support engineer",
          "99.99% uptime SLA"
        ]}
        current={props.currentTier === "enterprise"}
        cta={{ kind: "contact", label: "Talk to us", href: CONTACT_URL }}
      />

      <div className="panel">
        <button className="btn btn-secondary" onClick={props.onBack}>
          Back to dashboard
        </button>
      </div>

      {error ? <div className="error">{error}</div> : null}
      {info ? <div className="panel">{info}</div> : null}
    </>
  );
}

type CtaSpec =
  | null
  | { kind: "checkout"; label: string; disabled: boolean; onClick: () => void }
  | { kind: "contact"; label: string; href: string };

function TierCard(props: {
  name: string;
  sub: string;
  tag?: string;
  featured?: boolean;
  price: string;
  priceSub?: string;
  description: string;
  features: string[];
  current: boolean;
  cta: CtaSpec;
}) {
  return (
    <div
      className="panel"
      style={props.featured ? { borderColor: "var(--paper-dim, #888)" } : undefined}
    >
      {props.featured ? (
        <div
          style={{
            display: "inline-block",
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
            padding: "3px 8px",
            border: "1px solid currentColor",
            marginBottom: 10
          }}
        >
          Most popular
        </div>
      ) : null}
      <h3 className="section-title" style={{ fontSize: 18, marginBottom: 2 }}>
        {props.name}
        {props.current ? (
          <span style={{ color: "var(--verified)", marginLeft: 8 }}>(current)</span>
        ) : null}
      </h3>
      <p className="muted" style={{ marginTop: 0, marginBottom: 12 }}>
        {props.sub}
      </p>
      <p style={{ margin: "0 0 4px 0", fontSize: 24 }}>
        <strong>{props.price}</strong>
        {props.priceSub ? <span className="muted"> {props.priceSub}</span> : null}
      </p>
      <p className="muted" style={{ marginTop: 8, marginBottom: 12 }}>
        {props.description}
      </p>
      <ul style={{ paddingLeft: 18, color: "var(--paper-dim)" }}>
        {props.features.map((feature) => (
          <li key={feature}>{feature}</li>
        ))}
      </ul>
      {props.tag ? (
        <p className="muted" style={{ fontSize: 12 }}>
          {props.tag}
        </p>
      ) : null}
      {props.cta?.kind === "checkout" ? (
        <button className="btn" disabled={props.cta.disabled} onClick={props.cta.onClick}>
          {props.cta.label}
        </button>
      ) : null}
      {props.cta?.kind === "contact" ? (
        <a className="btn btn-secondary" href={props.cta.href}>
          {props.cta.label}
        </a>
      ) : null}
    </div>
  );
}
